import { resolveScreenFeatures, type ScreenFeatureSet } from '@skyweather/weather-core'
import type { LimitStatus, SiteUnits, SourceReading } from './types.js'
import { LimitDots } from './LimitDots.js'
import type { Observation } from '@skyweather/weather-core'
import {
  ageText,
  ceilingText,
  gustSpread,
  pressureText,
  tempDew,
  visibilityText,
  windDirection,
  windHeadline,
  windUnit,
} from './format.js'
import { formatWind } from '@skyweather/weather-core'
import { TREND_WINDOW_MS } from './api.js'
import { CHART_BOX, windChart } from './chart.js'
import { useSize } from './useSize.js'

interface Props {
  reading: SourceReading
  /** The owner's limits for this source, drawn as a row of dots directly above the wind direction. */
  limits: LimitStatus[]
  timeZone: string
  trend: Observation[]
  units: SiteUnits
  now: number
  /** What the owner has switched on (weather-core `screen-features.ts`); every key decided. Defaults: all on. */
  show?: ScreenFeatureSet
}

/**
 * The feed on screen: one source, with the owner's limits for it as coloured dots in a row of their own
 * directly above the wind direction in degrees (Kyle, 2026-10-02, K-194).
 * Never merged with another source (SKW-R-002). The panel carries no header of its own: the one
 * "Weather observations (<source>)" header sits over the whole screen (`ObservationsHeader.tsx`, K-292). A
 * source that has gone quiet keeps its last values, greyed: the screen never shows an old reading as
 * current, and never hides that the station is silent.
 */
export function SourcePanel({ reading, limits, timeZone, trend, units, now, show = resolveScreenFeatures(undefined) }: Props) {
  const o = reading.observation
  const silent = reading.health === 'not_reporting'
  const spread = gustSpread(o)
  const unit = windUnit(units)
  const facts: { label: string; value: string }[] = []
  const ceiling = ceilingText(o)
  if (ceiling) facts.push({ label: 'Ceiling', value: ceiling })
  const vis = visibilityText(o, units)
  if (vis) facts.push({ label: 'Visibility', value: vis })
  const td = tempDew(o, units)
  if (td) facts.push(td)
  const p = pressureText(reading, units)
  if (p) facts.push(p)
  const [chartRef, chartSize] = useSize<HTMLDivElement>()
  // Draw at the space the layout gives the chart (never smaller than legible), or the default box before
  // it is measured.
  const box = chartSize && chartSize.width > 200 && chartSize.height > 50
    ? { ...CHART_BOX, width: Math.round(chartSize.width), height: Math.round(chartSize.height) }
    : CHART_BOX
  const chart = windChart(trend, now - TREND_WINDOW_MS, now, unit, timeZone, box)
  const unitLabel = { kt: 'knots', mph: 'mph', kmh: 'km/h', mps: 'm/s' }[unit]
  return (
    <section className={`panel${silent ? ' panel--silent' : ''}${reading.health === 'late' ? ' panel--late' : ''}`}>
      {o === null ? (
        <p className="panel__empty">No reading yet from this source.</p>
      ) : (
        <>
          <div className="wind-top">
          <div className={`wind${silent ? ' wind--stale' : ''}`}>
            <span className="wind__value">{windHeadline(o, units)}</span>
            <span className="wind__side">
              {show.limitDots && <LimitDots limits={limits} units={units} timeZone={timeZone} />}
              <span className="wind__dir">
                {windDirection(o)}
                {silent ? ` · last heard ${ageText(o.observedAt, now)}` : ''}
              </span>
            </span>
          </div>

          {show.gustLullSpread && (o.gustKt !== null || o.lullKt !== null) && (
            <div className="wind-extra">
              {o.gustKt !== null && (
                <div>
                  <span className="label">Gust</span> <strong>{formatWind(o.gustKt, unit, { withKnots: false, role: 'gust' })}</strong>
                </div>
              )}
              {o.lullKt !== null && (
                <div>
                  <span className="label">Lull</span> <strong>{formatWind(o.lullKt, unit, { withKnots: false, role: 'lull' })}</strong>
                </div>
              )}
              {spread !== null && (
                <div>
                  <span className="label">Spread</span> <strong>{formatWind(spread, unit, { withKnots: false })}</strong>
                </div>
              )}
            </div>
          )}

          </div>

          {show.facts && facts.length > 0 && (
            <dl className="facts">
              {facts.map((f) => (
                <div key={f.label}>
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {show.windChart && chart.count >= 2 && (
            <figure className="trend">
              <div className="trend__box" ref={chartRef}>
              <svg viewBox={`0 0 ${box.width} ${box.height}`} role="img" aria-label="Wind and gust over the last 6 hours">
                {chart.speedLines.map((l) => (
                  <g key={`s${l.label}`}>
                    <line x1={chart.plot.x0} x2={chart.plot.x1} y1={l.y} y2={l.y} className={l.label === '0' ? 'trend__axis' : 'trend__grid'} />
                    <text x={chart.plot.x0 - 8} y={l.y} className="trend__label" textAnchor="end" dominantBaseline="central">{l.label}</text>
                  </g>
                ))}
                {chart.timeMarks.map((m) => (
                  <g key={`t${m.label}`}>
                    <line x1={m.x} x2={m.x} y1={chart.plot.y0} y2={chart.plot.y1} className="trend__grid trend__grid--time" />
                    <text x={m.x} y={chart.plot.y1 + 18} className="trend__label" textAnchor="middle">{m.label}</text>
                  </g>
                ))}
                <polyline points={chart.wind} className="trend__wind" />
                {chart.gust && <polyline points={chart.gust} className="trend__gust" />}
              </svg>
              </div>
              <figcaption>Last 6 h · {unitLabel} · solid line wind · dashed line gust</figcaption>
            </figure>
          )}
        </>
      )}

    </section>
  )
}
